"use client";

import type * as React from "react";
import { XClose } from "untitledui-js/react";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "@bidbattles/bloks/lib/ui";
import { motion } from "motion/react";

const DrawerPrimitiveContent = motion.create(DrawerPrimitive.Content);

function Drawer({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
	return (
		<DrawerPrimitive.Root data-slot="dialog" shouldScaleBackground {...props} />
	);
}

function DrawerTrigger({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
	return <DrawerPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DrawerPortal({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
	return <DrawerPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DrawerClose({
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
	return <DrawerPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DrawerOverlay({
	className,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
	return (
		<DrawerPrimitive.Overlay
			data-slot="dialog-overlay"
			className={cn("fixed inset-0 bg-black/10", className)}
			{...props}
		/>
	);
}

function DrawerHandle({
	className,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Handle>) {
	return (
		<DrawerPrimitive.Handle
			className={cn(
				"mx-auto w-12 h-1.5 flex-shrink-0 rounded-full !bg-gray-400 mt-2",
				className,
			)}
			{...props}
		/>
	);
}

function DrawerContent({
	className,
	children,
	handle,
	...props
}: React.ComponentProps<typeof DrawerPrimitiveContent> & {
	handle?: React.ReactNode;
	children?: React.ReactNode;
}) {
	return (
		<DrawerPortal data-slot="dialog-portal">
			<DrawerOverlay />
			<DrawerPrimitiveContent
				layout
				data-slot="dialog-content"
				className={cn(
					"rounded-t-lg bg-white flex flex-col mt-12 h-fit fixed bottom-0 left-0 right-0 outline-none",
					className,
				)}
				{...props}
			>
				{handle}
				<div className="p-4 md:p-box grow">{children}</div>
			</DrawerPrimitiveContent>
		</DrawerPortal>
	);
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="dialog-header"
			className={cn("flex flex-col gap-3", className)}
			{...props}
		/>
	);
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn(
				"flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
				className,
			)}
			{...props}
		/>
	);
}

function DrawerTitle({
	className,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
	return (
		<DrawerPrimitive.Title
			data-slot="dialog-title"
			className={cn(
				"text-2xl md:text-4xl leading-6 md:leading-11 font-semibold text-gray-900",
				className,
			)}
			{...props}
		/>
	);
}

function DrawerDescription({
	className,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
	return (
		<DrawerPrimitive.Description
			data-slot="dialog-description"
			className={cn(
				"text-sm md:text-base md:leading-6 text-gray-500",
				className,
			)}
			{...props}
		/>
	);
}

export {
	Drawer,
	DrawerClose,
	DrawerHandle,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
	DrawerPortal,
	DrawerTitle,
	DrawerTrigger,
};
